import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// useGroupComparisonBrief — AI cross-unit comparison brief lifecycle hook
// (Ship 7). The comparison analogue of useGroupReorderBrief.
//
//   1. On mount (and when groupId resolves), look up the row in
//      ai_group_briefs for (group_id, tab_key='comparison') where
//      generated_at > now() - 6h. Cache hit → phase='ready' (or 'empty'
//      when payload._empty). Miss → phase='generating', fire the edge fn.
//   2. regenerate({ bypassCache }) — explicit refresh past the window.
//   3. inFlightRef synchronous double-fire guard.
//   4. Calm deterministic fallback on error/empty — never the raw error.
//
// WINDOW NOTE: ai_group_briefs has NO window/date_bucket column — the cache
// key is (group_id, tab_key) only. A cached row could therefore be for a
// DIFFERENT window than the one now selected. So a window CHANGE is treated as
// a regenerate(bypassCache=true): we bypass the cache and recompute for the
// freshly-selected window. The brief's own `window` field disambiguates the
// rendered result. (Only the FIRST mount for a given window honors the cache.)
//
// DECOUPLED from useGroupComparison (the table data): the table must render
// even while this brief is generating / empty / failed.

const TAB_KEY = 'comparison'
const CACHE_WINDOW_HOURS = 6

export function useGroupComparisonBrief(groupId, window = '7d') {
  // --- All hooks declared BEFORE any conditional return (React #310). ---
  const [phase, setPhase]       = useState('idle')   // idle | generating | ready | empty | error
  const [brief, setBrief]       = useState(null)
  const [error, setError]       = useState(null)
  const [generatedAt, setGenAt] = useState(null)

  const inFlightRef = useRef(false)
  // The window the currently-loaded brief was fetched for. A change vs. the
  // selected `window` forces a bypass-cache regenerate.
  const loadedWindowRef = useRef(null)

  const lookupAndMaybeGenerate = useCallback(async ({ bypassCache = false } = {}) => {
    if (!groupId) return
    if (inFlightRef.current) return

    setError(null)

    // 1. Cache lookup (skipped on regenerate / bypass). Since there is no
    //    window column, the cached payload carries its own `window`; we only
    //    accept it when it matches the selected window.
    if (!bypassCache) {
      const cutoff = new Date(Date.now() - CACHE_WINDOW_HOURS * 60 * 60 * 1000).toISOString()
      const { data: rows, error: cacheErr } = await supabase
        .from('ai_group_briefs')
        .select('generated_at, payload')
        .eq('group_id', groupId)
        .eq('tab_key', TAB_KEY)
        .gte('generated_at', cutoff)
        .order('generated_at', { ascending: false })
        .limit(1)

      if (!cacheErr && rows && rows.length > 0) {
        const row = rows[0]
        const payload = row.payload || null
        const payloadWindow = payload && typeof payload.window === 'string' ? payload.window : null
        // Only honor the cache when it's for the selected window; otherwise
        // fall through to regenerate for the current window.
        if (payloadWindow == null || payloadWindow === window) {
          setBrief(payload)
          setGenAt(row.generated_at)
          setPhase(payload && payload._empty ? 'empty' : 'ready')
          loadedWindowRef.current = window
          return
        }
      }
      if (cacheErr) {
        // eslint-disable-next-line no-console
        console.warn('[useGroupComparisonBrief] cache read failed', cacheErr)
      }
    }

    // 2. Cache miss / bypass → call the edge fn. Claim the in-flight slot
    //    synchronously BEFORE the await.
    inFlightRef.current = true
    setPhase('generating')
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const resp = await fetch(`${supabaseUrl}/functions/v1/generate-group-comparison-brief`, {
        method: 'POST',
        headers: {
          'content-type':  'application/json',
          'authorization': `Bearer ${token}`,
          'apikey':        anonKey,
        },
        body: JSON.stringify({ group_id: groupId, window }),
      })
      if (!resp.ok) {
        const errText = await resp.text()
        throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
      }
      const data = await resp.json()
      if (data?.error) {
        setPhase('error')
        setError(data.message || "Couldn't pull your group comparison — tap to try again.")
        return
      }
      setBrief(data || null)
      setGenAt(new Date().toISOString())
      setPhase(data?._empty ? 'empty' : 'ready')
      loadedWindowRef.current = window
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useGroupComparisonBrief] edge fn call failed', e)
      setPhase('error')
      setError("Couldn't pull your group comparison — tap to try again.")
    } finally {
      inFlightRef.current = false
    }
  }, [groupId, window])

  // Initial load + window-change handling. A window CHANGE bypasses the cache
  // (no window column → recompute for the new window). The first load for a
  // window honors the cache.
  useEffect(() => {
    if (!groupId) return
    const windowChanged = loadedWindowRef.current != null && loadedWindowRef.current !== window
    lookupAndMaybeGenerate({ bypassCache: windowChanged })
  }, [groupId, window, lookupAndMaybeGenerate])

  const regenerate = useCallback(({ bypassCache = true } = {}) => {
    if (inFlightRef.current) return
    return lookupAndMaybeGenerate({ bypassCache })
  }, [lookupAndMaybeGenerate])

  return useMemo(
    () => ({ phase, brief, error, generatedAt, regenerate }),
    [phase, brief, error, generatedAt, regenerate],
  )
}
